import { Mui, MuiIcons } from '@leap4/frontend';
import React from 'react';
import { EmptyRow, rowSx } from './ruleRow';

const { Autocomplete, Button, IconButton, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip } = Mui;
const { Add, Close } = MuiIcons;

interface Filter {
    id: string;
    dimension: string;
    action: string;
    specTarget: { key: string; value: string }[];
}

interface FilterProps {
    dimensionsOptions: { key: string; value: string }[];
    filtersData: Filter[];
    onChange: (id: string, field: string, value: any) => void;
    onAddFilter: () => void;
    onDeleteFilter: (id: string) => void;
}

// Flex, not grid: the values field takes the rest of the line and drops to its own line when narrow.
const filterRowSx = { ...rowSx, display: 'flex', flexWrap: 'wrap' };

const FilterItem = ({ filter, dimensionsOptions, onChange, onDeleteFilter }: { filter: Filter; dimensionsOptions: FilterProps['dimensionsOptions']; onChange: FilterProps['onChange']; onDeleteFilter: FilterProps['onDeleteFilter'] }) => (
    <Mui.Box sx={filterRowSx}>
        <TextField select size="small" label="Dimension" sx={{ flex: '1 1 160px', maxWidth: { sm: 200 } }} value={filter.dimension} onChange={(e) => onChange(filter.id, 'dimension', e.target.value)}>
            {dimensionsOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                    {option.value}
                </MenuItem>
            ))}
        </TextField>

        <ToggleButtonGroup
            exclusive
            size="small"
            value={filter.action}
            onChange={(_e, value) => value && onChange(filter.id, 'action', value)}
            aria-label="Include or exclude these values"
            sx={{ flex: '0 0 auto', '& .MuiToggleButton-root': { px: 1.5, textTransform: 'none', fontSize: '0.8125rem' } }}
        >
            <ToggleButton value="include">Include</ToggleButton>
            <ToggleButton value="exclude">Exclude</ToggleButton>
        </ToggleButtonGroup>

        <Autocomplete
            multiple
            freeSolo
            autoSelect
            size="small"
            sx={{ flex: '1 1 200px', minWidth: 0 }}
            options={[]}
            value={filter.specTarget.map((t) => t.value)}
            onChange={(_event, newValue) => onChange(filter.id, 'specTarget', (newValue as string[]).map((v) => ({ key: v, value: v })))}
            renderInput={(params) => <TextField {...params} label="Values" placeholder={filter.specTarget.length ? '' : 'Type a value, press Enter'} />}
        />

        <Tooltip title="Remove filter">
            <IconButton size="small" sx={{ flex: 'none' }} onClick={() => onDeleteFilter(filter.id)} aria-label="Remove filter">
                <Close fontSize="small" />
            </IconButton>
        </Tooltip>
    </Mui.Box>
);

const Filters: React.FC<FilterProps> = ({ dimensionsOptions, filtersData, onChange, onAddFilter, onDeleteFilter }) =>
    filtersData.length === 0 ? (
        <EmptyRow text="No filters." action={<Button size="small" startIcon={<Add />} onClick={onAddFilter}>Add filter</Button>} />
    ) : (
        <Mui.Box sx={{ display: 'grid', gap: 1 }}>
            {filtersData.map((filter) => (
                <FilterItem key={filter.id} filter={filter} dimensionsOptions={dimensionsOptions} onChange={onChange} onDeleteFilter={onDeleteFilter} />
            ))}
            <Mui.Box>
                <Button size="small" startIcon={<Add />} onClick={onAddFilter}>
                    Add filter
                </Button>
            </Mui.Box>
        </Mui.Box>
    );

export default Filters;
